import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Badge,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Rating
} from '@mui/material';
import {
  Search,
  LocationOn,
  MyLocation,
  Person,
  FilterList,
  FavoriteBorder,
  Event,
  Favorite,
  Chat,
  Notifications,
  ShoppingCart,
  VerifiedUser,
  Sanitizer,
  Masks,
  SupportAgent,
  Apps,
  ContentCut,
  Face,
  Brush,
  Spa,
  Close,
  Male,
  Female
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import BottomNav from '../components/BottomNavigation';
import LocationPopup from '../components/LocationPopup';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [locationName, setLocationName] = useState('Select Location');
  
  // Mock banners data
  const banners = [
    {
      id: 1,
      title: 'Summer Special',
      subtitle: 'Get 20% off on all services',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=400&fit=crop',
      link: '/offers'
    },
    {
      id: 2,
      title: 'New Services',
      subtitle: 'Try our latest treatments',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=400&fit=crop',
      link: '/services'
    },
    {
      id: 3,
      title: 'Home Services',
      subtitle: 'Professional beauty at your doorstep',
      image: 'https://images.unsplash.com/photo-1552642084-9a907e5881ee?w=800&h=400&fit=crop',
      link: '/home-services'
    }
  ];

  // Mock categories data with icons and subcategories
  const categories = [
    { 
      id: 'all', 
      name: 'All', 
      icon: 'apps', 
      color: '#006AF9',
      subCategories: ['All Services', 'All Providers', 'All Products'],
      serviceCount: 150,
      providerCount: 45
    },
    { 
      id: 'hair', 
      name: 'Hair', 
      icon: 'content-cut', 
      color: '#FF6B35',
      subCategories: ['Haircut', 'Hair Color', 'Hair Styling', 'Hair Treatment'],
      serviceCount: 25,
      providerCount: 12
    },
    { 
      id: 'skincare', 
      name: 'Skincare', 
      icon: 'face', 
      color: '#4ECDC4',
      subCategories: ['Facial', 'Cleanup', 'Bleach', 'Anti-Aging'],
      serviceCount: 20,
      providerCount: 8
    },
    { 
      id: 'nails', 
      name: 'Nails', 
      icon: 'brush', 
      color: '#FF8B94',
      subCategories: ['Manicure', 'Pedicure', 'Nail Art', 'Gel Polish'],
      serviceCount: 15,
      providerCount: 6
    },
    { 
      id: 'massage', 
      name: 'Massage', 
      icon: 'spa', 
      color: '#9B59B6',
      subCategories: ['Swedish', 'Deep Tissue', 'Hot Stone', 'Aromatherapy'],
      serviceCount: 18,
      providerCount: 10
    },
    { 
      id: 'facial', 
      name: 'Facial', 
      icon: 'face', 
      color: '#3498DB',
      subCategories: ['Classic Facial', 'Anti-Aging', 'Acne Treatment', 'Hydrating'],
      serviceCount: 22,
      providerCount: 9
    },
    { 
      id: 'waxing', 
      name: 'Waxing', 
      icon: 'content-cut', 
      color: '#E74C3C',
      subCategories: ['Eyebrows', 'Upper Lip', 'Full Body', 'Bikini'],
      serviceCount: 12,
      providerCount: 5
    }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTab, setSelectedTab] = useState('salon');
  const [sortBy, setSortBy] = useState('');
  const [minRating, setMinRating] = useState(1);
  const [selectedGender, setSelectedGender] = useState([]);
  const [serviceMode, setServiceMode] = useState('in_salon');
  const [distance, setDistance] = useState(10);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [galleryIndices, setGalleryIndices] = useState({});
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Mock offers data
  const todaysOffers = [
    { id: 1, title: '20% Off Haircuts', image: 'https://via.placeholder.com/300x150/FF6B6B/FFFFFF?text=20%+Off+Haircuts' },
    { id: 2, title: 'Free Facial', image: 'https://via.placeholder.com/300x150/4ECDC4/FFFFFF?text=Free+Facial' }
  ];

  const thisWeekOffers = [
    { id: 3, title: 'Buy 2 Get 1 Free', image: 'https://via.placeholder.com/300x150/FFE66D/000000?text=Buy+2+Get+1+Free' },
    { id: 4, title: 'Student Discount', image: 'https://via.placeholder.com/300x150/FF8B94/FFFFFF?text=Student+Discount' }
  ];

  const thisMonthOffers = [
    { id: 5, title: 'Monthly Membership', image: 'https://via.placeholder.com/300x150/9B59B6/FFFFFF?text=Monthly+Membership' },
    { id: 6, title: 'Referral Bonus', image: 'https://via.placeholder.com/300x150/3498DB/FFFFFF?text=Referral+Bonus' }
  ];

  // Mock products data
  const products = [
    {
      id: 1,
      name: 'Professional Hair Shampoo',
      price: 25,
      description: 'Sulfate-free professional hair care',
      image: 'https://via.placeholder.com/300x200/FFE66D/000000?text=Shampoo',
      category: 'Hair Care',
      brand: 'ProCare',
      inStock: true,
      rating: 4.6,
      reviews: 89
    },
    {
      id: 2,
      name: 'Anti-Aging Serum',
      price: 65,
      description: 'Advanced anti-aging formula',
      image: 'https://via.placeholder.com/300x200/FF8B94/FFFFFF?text=Serum',
      category: 'Skincare',
      brand: 'BeautyLab',
      inStock: true,
      rating: 4.7,
      reviews: 234
    },
    {
      id: 3,
      name: 'Nail Polish Set',
      price: 15,
      description: 'Professional nail polish collection',
      image: 'https://via.placeholder.com/300x200/4ECDC4/FFFFFF?text=Nail+Polish',
      category: 'Nails',
      brand: 'NailPro',
      inStock: true,
      rating: 4.3,
      reviews: 156
    }
  ];

  // Mock services data
  const services = [
    {
      id: 1,
      name: 'Haircut & Styling',
      price: 45,
      duration: '45 min',
      description: 'Professional haircut and styling service',
      image: 'https://via.placeholder.com/300x200/006AF9/FFFFFF?text=Haircut',
      category: 'Hair',
      rating: 4.5,
      reviews: 128,
      inSalon: true,
      atHome: true,
      distance: 2.5
    },
    {
      id: 2,
      name: 'Facial Treatment',
      price: 80,
      duration: '60 min',
      description: 'Rejuvenating facial treatment',
      image: 'https://via.placeholder.com/300x200/FF6B6B/FFFFFF?text=Facial',
      category: 'Skincare',
      rating: 4.8,
      reviews: 95,
      inSalon: true,
      atHome: false,
      distance: 1.8
    },
    {
      id: 3,
      name: 'Manicure & Pedicure',
      price: 35,
      duration: '30 min',
      description: 'Complete nail care service',
      image: 'https://via.placeholder.com/300x200/4ECDC4/FFFFFF?text=Nails',
      category: 'Nails',
      rating: 4.3,
      reviews: 156,
      inSalon: true,
      atHome: true,
      distance: 3.2
    },
    {
      id: 4,
      name: 'Swedish Massage',
      price: 65,
      duration: '60 min',
      description: 'Relaxing Swedish massage therapy',
      image: 'https://via.placeholder.com/300x200/9B59B6/FFFFFF?text=Massage',
      category: 'Massage',
      rating: 4.7,
      reviews: 89,
      inSalon: true,
      atHome: true,
      distance: 2.1
    },
    {
      id: 5,
      name: 'Anti-Aging Facial',
      price: 95,
      duration: '75 min',
      description: 'Advanced anti-aging facial treatment',
      image: 'https://via.placeholder.com/300x200/3498DB/FFFFFF?text=Anti-Aging',
      category: 'Facial',
      rating: 4.9,
      reviews: 67,
      inSalon: true,
      atHome: false,
      distance: 1.5
    },
    {
      id: 6,
      name: 'Hair Coloring',
      price: 120,
      duration: '90 min',
      description: 'Professional hair coloring service',
      image: 'https://via.placeholder.com/300x200/E74C3C/FFFFFF?text=Hair+Color',
      category: 'Hair',
      rating: 4.6,
      reviews: 112,
      inSalon: true,
      atHome: false,
      distance: 2.8
    }
  ];

  useEffect(() => {
    loadUserLocation();
  }, []);

  // Auto-scroll gallery effect
  useEffect(() => {
    const interval = setInterval(() => {
      setGalleryIndices(prev => {
        const newIndices = {};
        Object.keys(prev).forEach(providerId => {
          newIndices[providerId] = (prev[providerId] + 1) % 3; // Assuming 3 images per gallery
        });
        return newIndices;
      });
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll banners effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBannerIndex(prev => (prev + 1) % banners.length);
    }, 4000); // Change banner every 4 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  const loadUserLocation = async () => {
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ latitude, longitude });
            setLocationName('Current Location');
          },
          (error) => {
            console.error('Location error:', error);
            setLocationName('Select Location');
          }
        );
      }
    } catch (error) {
      console.error('Location error:', error);
    }
  };

  const handleLocationPress = () => {
    setShowLocationPopup(true);
  };

  const handleLocationSelect = (location) => {
    setUserLocation(location);
    setLocationName(location.address);
  };

  const handleSearch = () => {
    navigate('/search', { state: { query: searchTerm } });
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    // TODO: Filter services/products by category
  };

  const handleServiceClick = (service) => {
    navigate(`/service/${service.id}`);
  };

  const handleAddToCart = (item, type) => {
    // TODO: Implement add to cart functionality
    console.log('Adding to cart:', item, type);
  };

  return (
    <Box sx={{ pb: 8, backgroundColor: '#F8FAFC' }}>
      {/* Header Section - Figma Design */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 50%, #60A5FA 100%)',
        color: 'white', 
        borderBottomLeftRadius: '25px',
        borderBottomRightRadius: '25px',
        minHeight: '200px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          transform: 'translate(50px, -50px)'
        }} />
        
        <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          p: 2, position: 'relative', zIndex: 1 
        }}>
          {/* Header Top - Logo and Icons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            {/* Logo on the left */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                letterSpacing: '0.5px'
              }}>
                BeautyPro
              </Typography>
            </Box>
            
            {/* Icons on the right */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton 
                color="inherit" 
                onClick={() => navigate('/chat')}
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <Chat />
              </IconButton>
              
              <IconButton 
                color="inherit" 
                onClick={() => navigate('/notifications')}
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <Badge badgeContent={3} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
              
              <IconButton 
                color="inherit" 
                onClick={() => navigate('/cart')}
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <Badge badgeContent={2} color="error">
                  <ShoppingCart />
                </Badge>
              </IconButton>
            </Box>
          </Box>
          
          {/* Location Widget - Figma Style */}
          <Box sx={{ mb: 3 }}>
            <Box 
              onClick={handleLocationPress}
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: '12px',
                p: 1.5,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ fontSize: '1.2rem', color: 'white' }} />
                <Typography sx={{ 
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}>
                  {userLocation ? '📍 Current Location' : '📍 Pick Location'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Search Bar - Figma Design */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ position: 'relative', flex: 1, maxWidth: 'calc(100% - 60px)' }}>
                <TextField
                  fullWidth
                  placeholder="🔍 Search for beauty services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  onFocus={() => setShowSearchDropdown(true)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#3B82F6', fontSize: '1.4rem' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={() => {
                            setSearchTerm('');
                            setShowSearchDropdown(false);
                          }}
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <Close sx={{ fontSize: '1.2rem', color: '#6B7280' }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                      height: 44,
                      fontSize: '0.85rem',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)',
                        borderWidth: '1px'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3B82F6',
                        borderWidth: '2px'
                      },
                      '& input': {
                        paddingLeft: '12px',
                        paddingRight: '12px'
                      }
                    },
                    '& .MuiInputBase-input': {
                      '&::placeholder': {
                        color: '#6B7280',
                        opacity: 1,
                        fontSize: '0.85rem'
                      }
                    }
                  }}
                />
                
                {/* Filter Button - Figma Style */}
                <Box sx={{ position: 'absolute', right: -45, top: 0 }}>
                  <IconButton 
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: '#3B82F6',
                      width: 44,
                      height: 44,
                      borderRadius: '10px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                        transform: 'scale(1.05)',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 6px 25px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    <FilterList sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
                
                                 {/* Search Dropdown - Quick Slices Only */}
                 {showSearchDropdown && (
                   <Box sx={{
                     position: 'absolute',
                     top: '100%',
                     left: 0,
                     right: 0,
                     backgroundColor: 'white',
                     borderRadius: 2,
                     boxShadow: 4,
                     zIndex: 1000,
                     mt: 1,
                     p: 2
                   }}>
                     <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1E3A8A' }}>
                       Popular Searches
                     </Typography>
                     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                       {['Facial', 'Haircut', 'Manicure', 'Massage'].map((filter) => (
                         <Chip
                           key={filter}
                           label={filter}
                           size="small"
                           onClick={() => {
                             setSearchTerm(filter);
                             setShowSearchDropdown(false);
                           }}
                           sx={{ 
                             fontSize: '0.75rem',
                             backgroundColor: '#f0f0f0',
                             '&:hover': { backgroundColor: '#1E3A8A', color: 'white' }
                           }}
                         />
                       ))}
                     </Box>
                                      </Box>
                 )}
                 
                                   {/* Filter Dropdown - Better Design */}
                  {showFilterDropdown && (
                    <Box sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: 4,
                      zIndex: 1000,
                      mt: 1,
                      p: 1.5,
                      maxHeight: 400,
                      overflowY: 'auto'
                    }}>
                      {/* Sort By Section */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1E3A8A', fontSize: '0.9rem' }}>
                          Sort By
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {[
                            { label: 'Price: Low to High', value: 'price_low' },
                            { label: 'Price: High to Low', value: 'price_high' },
                            { label: 'Rating: High to Low', value: 'rating_high' },
                            { label: 'Distance: Near to Far', value: 'distance_near' },
                            { label: 'Distance: Far to Near', value: 'distance_far' }
                          ].map((sort) => (
                            <Box key={sort.value} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              borderRadius: 1,
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}>
                              <Typography variant="body2" sx={{ color: '#333' }}>
                                {sort.label}
                              </Typography>
                              <Box
                                onClick={() => {
                                  setSortBy(sort.value);
                                  setShowFilterDropdown(false);
                                }}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  border: sortBy === sort.value ? '2px solid #1E3A8A' : '2px solid #ddd',
                                  backgroundColor: sortBy === sort.value ? '#1E3A8A' : 'transparent',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {sortBy === sort.value && (
                                  <Box sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    backgroundColor: 'white' 
                                  }} />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      
                      {/* Service Type Section */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1E3A8A', fontSize: '0.9rem' }}>
                          Service Type
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {['Facial', 'Haircut', 'Manicure', 'Massage', 'Waxing', 'Pedicure', 'Hair Color', 'Spa', 'Threading', 'Bleach', 'Cleanup'].map((filter) => (
                            <Chip
                              key={filter}
                              label={filter}
                              size="small"
                              onClick={() => {
                                setSearchTerm(filter);
                                setShowFilterDropdown(false);
                              }}
                              sx={{ 
                                fontSize: '0.75rem',
                                backgroundColor: '#f0f0f0',
                                '&:hover': { backgroundColor: '#1E3A8A', color: 'white' }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                      
                      {/* Gender Section */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1E3A8A' }}>
                          Gender
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {['Male', 'Female', 'Unisex'].map((gender) => (
                            <Box key={gender} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              borderRadius: 1,
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}>
                              <Typography variant="body2" sx={{ color: '#333' }}>
                                {gender}
                              </Typography>
                              <Box
                                onClick={() => {
                                  const genderLower = gender.toLowerCase();
                                  setSelectedGender(prev => 
                                    prev.includes(genderLower) 
                                      ? prev.filter(g => g !== genderLower)
                                      : [...prev, genderLower]
                                  );
                                }}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 2,
                                  border: selectedGender.includes(gender.toLowerCase()) ? '2px solid #1E3A8A' : '2px solid #ddd',
                                  backgroundColor: selectedGender.includes(gender.toLowerCase()) ? '#1E3A8A' : 'transparent',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {selectedGender.includes(gender.toLowerCase()) && (
                                  <Box sx={{ 
                                    width: 12, 
                                    height: 12, 
                                    backgroundColor: 'white',
                                    borderRadius: 1
                                  }} />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      
                      {/* Distance Section */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1E3A8A', fontSize: '0.9rem' }}>
                          Distance
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {[
                            { label: 'Within 1km', value: 1 },
                            { label: 'Within 3km', value: 3 },
                            { label: 'Within 5km', value: 5 },
                            { label: 'Within 10km', value: 10 },
                            { label: 'Any distance', value: 0 }
                          ].map((distance) => (
                            <Box key={distance.value} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              borderRadius: 1,
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}>
                              <Typography variant="body2" sx={{ color: '#333' }}>
                                {distance.label}
                              </Typography>
                              <Box
                                onClick={() => {
                                  setDistance(distance.value);
                                  setShowFilterDropdown(false);
                                }}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  border: distance === distance.value ? '2px solid #1E3A8A' : '2px solid #ddd',
                                  backgroundColor: distance === distance.value ? '#1E3A8A' : 'transparent',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {distance === distance.value && (
                                  <Box sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    backgroundColor: 'white' 
                                  }} />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      
                      {/* Service Mode Section */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1E3A8A', fontSize: '0.9rem' }}>
                          Service Mode
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {['In Salon', 'At Home'].map((mode) => (
                            <Box key={mode} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              borderRadius: 1,
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}>
                              <Typography variant="body2" sx={{ color: '#333' }}>
                                {mode}
                              </Typography>
                              <Box
                                onClick={() => {
                                  setServiceMode(mode.toLowerCase().replace(' ', '_'));
                                  setShowFilterDropdown(false);
                                }}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  border: serviceMode === mode.toLowerCase().replace(' ', '_') ? '2px solid #1E3A8A' : '2px solid #ddd',
                                  backgroundColor: serviceMode === mode.toLowerCase().replace(' ', '_') ? '#1E3A8A' : 'transparent',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {serviceMode === mode.toLowerCase().replace(' ', '_') && (
                                  <Box sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    backgroundColor: 'white' 
                                  }} />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      
                      {/* Minimum Rating Section */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1E3A8A' }}>
                          Minimum Rating
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <Box key={rating} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              borderRadius: 1,
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}>
                              <Typography variant="body2" sx={{ color: '#333' }}>
                                {rating}+ Stars
                              </Typography>
                              <Box
                                onClick={() => {
                                  setMinRating(rating);
                                  setShowFilterDropdown(false);
                                }}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  border: minRating === rating ? '2px solid #1E3A8A' : '2px solid #ddd',
                                  backgroundColor: minRating === rating ? '#1E3A8A' : 'transparent',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {minRating === rating && (
                                  <Box sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    backgroundColor: 'white' 
                                  }} />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      
                      {/* Distance Section */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1E3A8A' }}>
                          Distance (km)
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {[5, 10, 15, 20, 30].map((dist) => (
                            <Box key={dist} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              borderRadius: 1,
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}>
                              <Typography variant="body2" sx={{ color: '#333' }}>
                                Within {dist}km
                              </Typography>
                              <Box
                                onClick={() => {
                                  setDistance(dist);
                                  setShowFilterDropdown(false);
                                }}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  border: distance === dist ? '2px solid #1E3A8A' : '2px solid #ddd',
                                  backgroundColor: distance === dist ? '#1E3A8A' : 'transparent',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {distance === dist && (
                                  <Box sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    backgroundColor: 'white' 
                                  }} />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  )}
               </Box>
             </Box>
           </Box>

          {/* Quick Search Chips - Figma Style */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {['✨ Facial', '💇‍♀️ Haircut', '💅 Manicure', '💆‍♀️ Massage', '🧖‍♀️ Waxing'].map((option) => (
                <Chip
                  key={option}
                  label={option}
                  size="small"
                  onClick={() => setSearchTerm(option.replace(/[✨💇‍♀️💅💆‍♀️🧖‍♀️]/g, '').trim())}
                  sx={{ 
                    fontSize: '0.8rem',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '20px',
                    fontWeight: 500,
                    backdropFilter: 'blur(10px)',
                    '&:hover': { 
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      border: '1px solid rgba(255,255,255,0.5)',
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Banners - Auto-scrolling Full Width */}
                      <Box sx={{ mb: 3, mx: -2 }}>
              <Box sx={{ 
                position: 'relative',
                height: 120,
                overflow: 'hidden',
                borderRadius: '16px',
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
              }}>
              <Box sx={{
                display: 'flex',
                transition: 'transform 0.8s ease-in-out',
                transform: `translateX(-${currentBannerIndex * 100}%)`,
                width: `${banners.length * 100}%`
              }}>
                                  {banners.map((banner, index) => (
                    <Box
                      key={banner.id}
                      sx={{
                        width: `${100 / banners.length}%`,
                        height: 120,
                        position: 'relative',
                        cursor: 'pointer',
                        backgroundImage: `url(${banner.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}
                      onClick={() => navigate(banner.link)}
                    >
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                        color: 'white',
                        p: 3
                      }}
                    >
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        mb: 0.5, 
                        fontSize: '1.2rem',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                      }}>
                        {banner.title}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        opacity: 0.95, 
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        {banner.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              
              {/* Banner Indicators */}
              {banners.length > 1 && (
                <Box sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 0.5
                }}>
                  {banners.map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: index === currentBannerIndex ? 'white' : 'rgba(255,255,255,0.5)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => setCurrentBannerIndex(index)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

             <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: 2 
        }}>



        {/* Categories - Figma Design */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ 
            mb: 3, 
            fontWeight: 700, 
            color: '#1F2937', 
            px: 2,
            fontSize: '1.5rem',
            textAlign: 'center'
          }}>
            ✨ Popular Categories
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            overflowX: 'auto', 
            pb: 2,
            px: 2,
            '&::-webkit-scrollbar': { 
              height: 4,
              backgroundColor: 'rgba(0,0,0,0.05)'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: 2
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0,0,0,0.2) transparent'
          }}>
            {categories.map((category, index) => {
              const isSelected = selectedCategory === category.id;
              
              return (
                <Box
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  sx={{
                    width: 140,
                    height: 160,
                    position: 'relative',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.05)',
                      '& .category-card': {
                        boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
                      }
                    }
                  }}
                >
                  <Box 
                    className="category-card"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: isSelected 
                        ? 'linear-gradient(135deg, #F59E0B, #F97316)' 
                        : 'linear-gradient(135deg, #FFFFFF, #F8FAFC)',
                      borderRadius: '16px',
                      boxShadow: isSelected 
                        ? '0 8px 25px rgba(245, 158, 11, 0.3)' 
                        : '0 4px 20px rgba(0,0,0,0.08)',
                      p: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      border: isSelected ? '2px solid #F59E0B' : '1px solid rgba(0,0,0,0.08)'
                    }}
                  >
                    {/* Icon */}
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      backgroundColor: isSelected 
                        ? 'rgba(255,255,255,0.9)' 
                        : 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                    }}>
                      {category.icon === 'apps' && <Apps sx={{ fontSize: '1.5rem', color: isSelected ? '#F59E0B' : '#FFFFFF' }} />}
                      {category.icon === 'content-cut' && <ContentCut sx={{ fontSize: '1.5rem', color: isSelected ? '#F59E0B' : '#FFFFFF' }} />}
                      {category.icon === 'face' && <Face sx={{ fontSize: '1.5rem', color: isSelected ? '#F59E0B' : '#FFFFFF' }} />}
                      {category.icon === 'brush' && <Brush sx={{ fontSize: '1.5rem', color: isSelected ? '#F59E0B' : '#FFFFFF' }} />}
                      {category.icon === 'spa' && <Spa sx={{ fontSize: '1.5rem', color: isSelected ? '#F59E0B' : '#FFFFFF' }} />}
                    </Box>
                    
                    {/* Category Name */}
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: isSelected ? '#1F2937' : '#374151',
                      letterSpacing: 0.3,
                      mb: 0.5,
                      textAlign: 'center'
                    }}>
                      {category.name}
                    </Typography>
                    
                    {/* Subcategories */}
                    <Typography variant="body2" sx={{ 
                      fontSize: '0.7rem',
                      color: isSelected ? '#6B7280' : '#9CA3AF',
                      fontWeight: 500,
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      lineHeight: 1.2
                    }}>
                      {category.subCategories.slice(0, 3).join(' • ')}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

                 {/* Salon/Pros Toggle - Figma Design */}
         <Box sx={{ mb: 4, px: 2 }}>
           <Box sx={{ 
             display: 'flex', 
             backgroundColor: 'rgba(255,255,255,0.1)', 
             borderRadius: '16px', 
             p: 0.5,
             boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
             backdropFilter: 'blur(10px)',
             border: '1px solid rgba(255,255,255,0.2)'
           }}>
             <Button
               fullWidth
               variant={selectedTab === 'salon' ? 'contained' : 'text'}
               startIcon={<Event sx={{ fontSize: '1.2rem' }} />}
               onClick={() => setSelectedTab('salon')}
               sx={{
                 borderRadius: '12px',
                 textTransform: 'none',
                 color: selectedTab === 'salon' ? 'white' : '#1F2937',
                 backgroundColor: selectedTab === 'salon' ? '#3B82F6' : 'transparent',
                 fontWeight: 600,
                 fontSize: '0.95rem',
                 py: 1.5,
                 transition: 'all 0.3s ease',
                 '&:hover': {
                   backgroundColor: selectedTab === 'salon' ? '#2563EB' : 'rgba(255,255,255,0.1)',
                   transform: 'scale(1.02)'
                 }
               }}
             >
               🏪 Salon
             </Button>
             <Button
               fullWidth
               variant={selectedTab === 'pros' ? 'contained' : 'text'}
               startIcon={<Person sx={{ fontSize: '1.2rem' }} />}
               onClick={() => setSelectedTab('pros')}
               sx={{
                 borderRadius: '12px',
                 textTransform: 'none',
                 color: selectedTab === 'pros' ? 'white' : '#1F2937',
                 backgroundColor: selectedTab === 'pros' ? '#3B82F6' : 'transparent',
                 fontWeight: 600,
                 fontSize: '0.95rem',
                 py: 1.5,
                 transition: 'all 0.3s ease',
                 '&:hover': {
                   backgroundColor: selectedTab === 'pros' ? '#2563EB' : 'rgba(255,255,255,0.1)',
                   transform: 'scale(1.02)'
                 }
               }}
             >
               👩‍💼 Pros
             </Button>
           </Box>
         </Box>

         {/* Recommended Providers - Figma Design */}
         <Box sx={{ mb: 4, px: 2 }}>
           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
             <Typography variant="h5" sx={{ 
               fontWeight: 700, 
               color: '#1F2937',
               fontSize: '1.4rem'
             }}>
               👥 Recommended Providers
             </Typography>
             <Button
               variant="text"
               onClick={() => navigate('/providers')}
               sx={{ 
                 color: '#3B82F6', 
                 textTransform: 'none', 
                 fontWeight: 600,
                 fontSize: '0.95rem',
                 '&:hover': {
                   backgroundColor: 'rgba(59,130,246,0.1)',
                   borderRadius: '8px'
                 }
               }}
             >
               View All →
             </Button>
           </Box>
           <Box sx={{ 
             display: 'flex', 
             gap: 2.5, 
             overflowX: 'auto', 
             pb: 2,
             px: 1,
             '&::-webkit-scrollbar': { 
               height: 4,
               backgroundColor: 'rgba(0,0,0,0.05)'
             },
             '&::-webkit-scrollbar-thumb': {
               backgroundColor: 'rgba(0,0,0,0.2)',
               borderRadius: 2
             },
             '&::-webkit-scrollbar-track': {
               backgroundColor: 'transparent'
             },
             scrollbarWidth: 'thin',
             scrollbarColor: 'rgba(0,0,0,0.2) transparent'
           }}>
             {[
               {
                 id: 1,
                 name: 'Sarah Johnson',
                 specialty: 'Hair Stylist',
                 rating: 4.8,
                 reviews: 156,
                 image: 'https://via.placeholder.com/120x120/FF6B6B/FFFFFF?text=SJ',
                 distance: 2.1,
                 price: 'From $45',
                 isOnline: true,
                 gender: 'female',
                 gallery: [
                   'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=240&h=160&fit=crop&crop=face',
                   'https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=240&h=160&fit=crop&crop=face',
                   'https://images.unsplash.com/photo-1552642084-9a907e5881ee?w=240&h=160&fit=crop&crop=face'
                 ]
               },
               {
                 id: 2,
                 name: 'Maria Garcia',
                 specialty: 'Facial Specialist',
                 rating: 4.9,
                 reviews: 203,
                 image: 'https://via.placeholder.com/120x120/4ECDC4/FFFFFF?text=MG',
                 distance: 1.8,
                 price: 'From $65',
                 isOnline: true,
                 gender: 'female',
                 gallery: [
                   'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=240&h=160&fit=crop&crop=face',
                   'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=240&h=160&fit=crop&crop=face',
                   'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=240&h=160&fit=crop&crop=face'
                 ]
               },
               {
                 id: 3,
                 name: 'Lisa Chen',
                 specialty: 'Nail Artist',
                 rating: 4.7,
                 reviews: 89,
                 image: 'https://via.placeholder.com/120x120/9B59B6/FFFFFF?text=LC',
                 distance: 3.2,
                 price: 'From $35',
                 isOnline: false,
                 gender: 'female',
                 gallery: [
                   'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=240&h=160&fit=crop&crop=face',
                   'https://images.unsplash.com/photo-1632345031435-8727f6897d53?w=240&h=160&fit=crop&crop=face',
                   'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=240&h=160&fit=crop&crop=face'
                 ]
               },
               {
                 id: 4,
                 name: 'Emma Wilson',
                 specialty: 'Massage Therapist',
                 rating: 4.6,
                 reviews: 134,
                 image: 'https://via.placeholder.com/120x120/3498DB/FFFFFF?text=EW',
                 distance: 2.5,
                 price: 'From $55',
                 isOnline: true,
                 gender: 'female',
                 gallery: [
                   'https://images.unsplash.com/photo-1544161512-6f99a46013b4?w=240&h=160&fit=crop&crop=face',
                   'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=240&h=160&fit=crop&crop=face',
                   'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=240&h=160&fit=crop&crop=face'
                 ]
               }
             ].map((provider) => (
               <Card 
                 key={provider.id}
                 sx={{ 
                   minWidth: 200,
                   maxWidth: 200,
                   cursor: 'pointer',
                   borderRadius: '16px',
                   transition: 'all 0.3s ease',
                   boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                   border: '1px solid rgba(0,0,0,0.06)',
                   flexShrink: 0,
                   overflow: 'hidden',
                   backgroundColor: '#FFFFFF',
                   '&:hover': {
                     transform: 'translateY(-6px) scale(1.03)',
                     boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                     border: '1px solid rgba(59,130,246,0.2)'
                   }
                 }}
                 onClick={() => navigate(`/provider/${provider.id}`)}
               >
                 <CardContent sx={{ p: 0, position: 'relative' }}>
                   {/* Auto-scrolling Image Gallery */}
                   <Box sx={{ 
                     position: 'relative',
                     height: 180,
                     overflow: 'hidden'
                   }}>
                     {provider.gallery && provider.gallery.length > 0 && (
                       <Box sx={{
                         display: 'flex',
                         transition: 'transform 0.5s ease-in-out',
                         transform: `translateX(-${(galleryIndices[provider.id] || 0) * 100}%)`,
                         width: `${provider.gallery.length * 100}%`
                       }}>
                         {provider.gallery.map((image, index) => (
                           <Box
                             key={index}
                             sx={{
                               width: `${100 / provider.gallery.length}%`,
                               height: 180,
                               backgroundImage: `url(${image})`,
                               backgroundSize: 'cover',
                               backgroundPosition: 'center',
                               backgroundRepeat: 'no-repeat'
                             }}
                           />
                         ))}
                       </Box>
                     )}
                     
                     {/* Gallery Indicators */}
                     {provider.gallery && provider.gallery.length > 1 && (
                       <Box sx={{
                         position: 'absolute',
                         bottom: 8,
                         left: '50%',
                         transform: 'translateX(-50%)',
                         display: 'flex',
                         gap: 0.5
                       }}>
                         {provider.gallery.map((_, index) => (
                           <Box
                             key={index}
                             sx={{
                               width: 6,
                               height: 6,
                               borderRadius: '50%',
                               backgroundColor: index === (galleryIndices[provider.id] || 0) ? 'white' : 'rgba(255,255,255,0.6)',
                               transition: 'all 0.3s ease'
                             }}
                           />
                         ))}
                       </Box>
                     )}
                     
                     {/* Online Status Indicator */}
                     <Box sx={{
                       position: 'absolute',
                       top: 12,
                       right: 12,
                       width: 10,
                       height: 10,
                       borderRadius: '50%',
                       backgroundColor: provider.isOnline ? '#10B981' : '#9CA3AF',
                       border: '2px solid white',
                       boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                       zIndex: 1
                     }} />
                   </Box>
                   
                   {/* Provider Info */}
                   <Box sx={{ p: 2, textAlign: 'left' }}>
                     {/* Name and Gender */}
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                       <Typography variant="h6" component="h3" sx={{ 
                         fontSize: '1rem', 
                         fontWeight: 700, 
                         color: '#1F2937', 
                         letterSpacing: 0.3,
                         flex: 1
                       }}>
                         {provider.name}
                       </Typography>
                       {provider.gender === 'male' ? (
                         <Male sx={{ fontSize: '0.9rem', color: '#3B82F6' }} />
                       ) : provider.gender === 'female' ? (
                         <Female sx={{ fontSize: '0.9rem', color: '#EC4899' }} />
                       ) : null}
                     </Box>
                     
                     {/* Rating and Distance Row */}
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                         <Rating 
                           value={provider.rating} 
                           precision={0.1} 
                           size="small" 
                           readOnly 
                           sx={{ 
                             '& .MuiRating-iconFilled': { color: '#F59E0B' },
                             '& .MuiRating-iconEmpty': { color: '#E5E7EB' }
                           }} 
                         />
                         <Typography variant="body2" sx={{ 
                           fontSize: '0.75rem', 
                           color: '#6B7280',
                           fontWeight: 500
                         }}>
                           ({provider.reviews})
                         </Typography>
                       </Box>
                       
                       <Typography variant="body2" sx={{ 
                         fontSize: '0.75rem', 
                         color: '#6B7280',
                         fontWeight: 500
                       }}>
                         📍 {provider.distance}km
                       </Typography>
                     </Box>
                   </Box>
                 </CardContent>
               </Card>
             ))}
           </Box>
         </Box>

        {/* Featured Services - Figma Design */}
        <Box sx={{ mb: 4, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              color: '#1F2937',
              fontSize: '1.4rem'
            }}>
              ✨ Featured Services
            </Typography>
            <Button
              variant="text"
              onClick={() => navigate('/services')}
              sx={{ 
                color: '#3B82F6', 
                textTransform: 'none', 
                fontWeight: 600,
                fontSize: '0.95rem',
                '&:hover': {
                  backgroundColor: 'rgba(59,130,246,0.1)',
                  borderRadius: '8px'
                }
              }}
            >
              View All →
            </Button>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 2.5, 
            overflowX: 'auto', 
            pb: 2,
            px: 1,
            '&::-webkit-scrollbar': { 
              height: 4,
              backgroundColor: 'rgba(0,0,0,0.05)'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: 2
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0,0,0,0.2) transparent'
          }}>
            {services.slice(0, 5).map((service) => (
              <Card 
                key={service.id}
                sx={{ 
                  minWidth: 180,
                  maxWidth: 180,
                  cursor: 'pointer',
                  borderRadius: '16px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  flexShrink: 0,
                  overflow: 'hidden',
                  backgroundColor: '#FFFFFF',
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.03)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                    border: '1px solid rgba(59,130,246,0.2)'
                  }
                }}
                onClick={() => handleServiceClick(service)}
              >
                <CardMedia
                  component="img"
                  height="120"
                  image={service.image}
                  alt={service.name}
                  sx={{ 
                    objectFit: 'cover'
                  }}
                />
                <CardContent sx={{ p: 2, position: 'relative' }}>
                  <Typography variant="h6" component="h3" sx={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 700, 
                    color: '#1F2937', 
                    mb: 0.5,
                    letterSpacing: 0.3,
                    lineHeight: 1.2
                  }}>
                    {service.name}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontSize: '0.75rem', 
                    fontWeight: 600,
                    color: '#6B7280'
                  }}>
                    ⏱️ {service.duration}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Rating 
                        value={service.rating} 
                        precision={0.5} 
                        size="small" 
                        readOnly 
                        sx={{ 
                          '& .MuiRating-iconFilled': { color: '#F59E0B' },
                          '& .MuiRating-iconEmpty': { color: '#E5E7EB' }
                        }} 
                      />
                      <Typography variant="body2" sx={{ 
                        fontSize: '0.7rem', 
                        color: '#6B7280',
                        fontWeight: 500
                      }}>
                        ({service.reviews})
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      fontSize: '0.9rem',
                      color: '#3B82F6',
                      letterSpacing: 0.3
                    }}>
                      ${service.price}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      '&:hover': { 
                        backgroundColor: '#2563EB',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(service, 'service');
                    }}
                  >
                    <FavoriteBorder fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Today's Offers */}
        {todaysOffers.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
              Today's Offers
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
              {todaysOffers.map((offer) => (
                <Card 
                  key={offer.id}
                  sx={{ 
                    minWidth: 300,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/offers')}
                >
                  <CardMedia
                    component="img"
                    height="150"
                    image={offer.image}
                    alt={offer.title}
                  />
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* This Week's Offers */}
        {thisWeekOffers.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
              This Week's Offers
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
              {thisWeekOffers.map((offer) => (
                <Card 
                  key={offer.id}
                  sx={{ 
                    minWidth: 300,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/offers')}
                >
                  <CardMedia
                    component="img"
                    height="150"
                    image={offer.image}
                    alt={offer.title}
                  />
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* This Month's Offers */}
        {thisMonthOffers.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
              This Month's Offers
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
              {thisMonthOffers.map((offer) => (
                <Card 
                  key={offer.id}
                  sx={{ 
                    minWidth: 300,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/offers')}
                >
                  <CardMedia
                    component="img"
                    height="150"
                    image={offer.image}
                    alt={offer.title}
                  />
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Featured Products */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
              Featured Products
            </Typography>
            <Button
              variant="text"
              onClick={() => navigate('/products')}
              sx={{ color: '#1E3A8A', textTransform: 'none', fontWeight: 600 }}
            >
              See All
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
            {products.slice(0, 5).map((product) => (
              <Card 
                key={product.id}
                sx={{ 
                  minWidth: 200,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <CardMedia
                  component="img"
                  height="120"
                  image={product.image}
                  alt={product.name}
                />
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" component="h3" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1F2937', mb: 1 }}>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {product.brand}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Rating value={product.rating} precision={0.5} size="small" readOnly />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                      ({product.reviews})
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="#1E3A8A" sx={{ fontWeight: 600 }}>
                    ${product.price}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      backgroundColor: '#1E3A8A',
                      color: 'white',
                      '&:hover': { backgroundColor: '#1E3A8A' }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(product, 'product');
                    }}
                  >
                    <ShoppingCart fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Safety Icons */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937', textAlign: 'center' }}>
            Your Safety is Our Priority
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <VerifiedUser sx={{ fontSize: 32, color: '#4CAF50', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Verified Providers
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Sanitizer sx={{ fontSize: 32, color: '#2196F3', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Sanitized Equipment
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Masks sx={{ fontSize: 32, color: '#FF9800', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Safety Protocols
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <SupportAgent sx={{ fontSize: 32, color: '#9C27B0', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  24/7 Support
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Location Popup */}
      <LocationPopup 
        open={showLocationPopup}
        onClose={() => setShowLocationPopup(false)}
        onLocationSelect={handleLocationSelect}
      />
    </Box>
  );
};

export default Home; 